
import { useQuery } from '@tanstack/react-query';
import secureLocalStorage from 'react-secure-storage';

import { serverRequest } from "../../util/server_request";
import { extractErrorMessage } from '../extractErrorMessage';
import { notifyUser } from '../../util/util';

const notifiedErrors = new Set();

// Helper function to notify only once for the same error
const notifyOnce = (type, message, title = "") => {
  if (!notifiedErrors.has(message)) {
    notifiedErrors.add(message);
    notifyUser(type, message, title);
  }
};

export function usePolicyReport(policyNumber, year = '', options = {}) {
  console.log('Hook called with policyNumber:', policyNumber, 'year:', year);
  
  const { enabled = true } = options;
  
  return useQuery({
    queryKey: ['policy-report', policyNumber, year],
    queryFn: async () => {
      console.log('Hook query function executing');
      try {
        if (!policyNumber) {
          throw new Error('Policy number is required');
        }

        console.log('Making GET request to policy-reports');
        
        // Build query parameters using filter[policy_number] format
        const params = new URLSearchParams();
        params.append('filter[policy_number]', policyNumber);
        if (year) {
          params.append('filter[year]', year);
        }
        
        const endpoint = `generated-policy-reports?${params.toString()}`;
        const response = await serverRequest('GET', endpoint);
        
        console.log('GET response:', response);
        
        // Handle API errors
        if (response?.error) {
          const errorMessages = {
            "You have exceeded your Requests.": {
              message: 'Request limit exceeded. Please try again later.',
              type: 'warning',
              title: 'Rate Limit'
            },
            "Not Found.": {
              message: 'The requested reports could not be found.',
              type: 'error',
              title: 'Not Found'
            }
          };

          const errorConfig = errorMessages[response.error] || {
            message: response.error,
            type: 'error',
            title: 'Error'
          };

          notifyOnce(errorConfig.type, errorConfig.message, errorConfig.title);
          throw new Error(errorConfig.message);
        }

        if (!response?.success) {
          const errorMessage = response?.message || 'Failed to initiate policy report';
          notifyOnce('error', errorMessage, 'Error');
          throw new Error(errorMessage);
        }

        // Normalize response data - data is an array of reports
        const normalizedResponse = {
          ...response,
          data: Array.isArray(response.data) 
            ? response.data.map(report => ({
                ...report,
                id: report.id || report.report_id,
                report_id: report.report_id || report.id,
              }))
            : response.data ? [{
                ...response.data,
                id: response.data.id || response.data.report_id,
                report_id: response.data.report_id || response.data.id,
              }] : []
        };

        // Return the response with normalized reports array
        return normalizedResponse;
        
      } catch (error) {
        if (error?.message?.toLowerCase().includes('exceeded your requests')) {
          const limitMessage = 'Request limit exceeded. Please try again later.';
          notifyOnce("warning", limitMessage, "Rate Limit");
          throw new Error(limitMessage);
        }
        
        const errorMessage = extractErrorMessage(error, 'Failed to fetch policy report');
        notifyOnce("error", errorMessage, "Error");
        throw new Error(errorMessage);
      }
    },
    enabled: !!secureLocalStorage.getItem('token') && !!policyNumber && enabled,
    staleTime: 30 * 1000, // 30 seconds
    retry: false, // Don't retry - let user manually refresh
  });
}
